import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Check, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/residentApi";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/sa/media-voices`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("nex_token")}` });
const DESC = { onyx: "Deep, commanding", echo: "Clear and direct", ash: "Bold and gritty", coral: "Warm and friendly", sage: "Calm and steady", alloy: "Neutral and even", nova: "Bright and upbeat", shimmer: "Soft and gentle" };
const cap = (v) => v[0].toUpperCase() + v.slice(1);

/** Each video keeps its own narrator: picking a voice here re-records only that video. */
export default function MediaVoices() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const audio = useRef(null);
  const load = useCallback(() => axios.get(BASE, { headers: headers() }).then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); return () => audio.current?.pause(); }, [load]);
  const running = d?.videos.some((v) => v.job?.state === "running");
  useEffect(() => { if (!running) return undefined; const id = setInterval(load, 4000); return () => clearInterval(id); }, [running, load]);
  const preview = async (v) => {
    audio.current?.pause(); setBusy(`p-${v}`);
    try { const r = await axios.get(`${BASE}/sample/${v}`, { headers: headers(), responseType: "blob", timeout: 60000 }); audio.current = new Audio(URL.createObjectURL(r.data)); audio.current.play().catch(() => {}); } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  const pick = (video, v) => axios.put(`${BASE}/${video.key}/${v}`, {}, { headers: headers() })
    .then(() => { toast.success(`${video.label}: re-recording with ${cap(v)} · about a minute`); load(); }).catch((e) => toast.error(errMsg(e)));
  if (!d) return null;
  return (
    <div className="space-y-4" data-testid="media-voices">
      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-nex-gold">Voices for the other videos · each one separate</div>
      {d.videos.map((video) => (
        <div key={video.key} className="space-y-2" data-testid={`media-voice-${video.key}`}>
          <div className="flex items-center gap-2 text-sm text-nex-silver">
            <span className="font-semibold">{video.label}</span><span className="text-nex-muted">· {cap(video.voice)}</span>
            {video.job?.state === "running" && <span className="inline-flex items-center gap-1 text-[11px] text-nex-gold" data-testid={`media-voice-${video.key}-running`}><Loader2 className="h-3 w-3 animate-spin" /> Re-recording…</span>}
            {video.job?.state === "done" && <span className="text-[11px] text-emerald-300" data-testid={`media-voice-${video.key}-done`}>Ready · visitors now hear {cap(video.job.voice)}</span>}
            {video.job?.state === "error" && <span className="text-[11px] text-red-300" data-testid={`media-voice-${video.key}-error`}>Couldn't re-record: {video.job.error}</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {d.voices.map((v) => {
              const on = v === video.voice;
              return (
                <div key={v} className={`border p-2 ${on ? "border-nex-gold bg-nex-gold/10" : "border-white/10"}`}>
                  <button type="button" disabled={running} onClick={() => !on && pick(video, v)} data-testid={`media-voice-${video.key}-pick-${v}`} className="w-full text-left disabled:opacity-60">
                    <span className="flex items-center gap-1 text-[13px] text-nex-silver font-semibold">{on && <Check className="h-3 w-3 text-nex-gold" />}{cap(v)}</span>
                    <span className="block text-[10px] text-nex-muted">{DESC[v]}</span>
                  </button>
                  <button type="button" onClick={() => preview(v)} disabled={busy === `p-${v}`} data-testid={`media-voice-${video.key}-preview-${v}`} className="mt-1 inline-flex items-center gap-1 text-[10px] text-nex-gold hover:underline disabled:opacity-60">
                    {busy === `p-${v}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Volume2 className="h-3 w-3" />} Preview
                  </button>
                </div>);
            })}
          </div>
        </div>))}
    </div>
  );
}
